import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "../../../permissions/usePermission";
import dummyImage from "../../../images/user.png";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import { setNetworkRestoreOnce } from "./networkStateKeys";
import { exportNetworkMembersCsv } from "./networkExport";

const COMMITTEE_ORDER = [
  "Operation Committee",
  "Program Head",
  "Other Members",
];

const MARATHWADA_A_REGION = "Marathwada A";

const MARATHWADA_A_COMMITTEE_ORDER = [
  "Operation Committee",
  "Program Head",
  "Operation Committee (Female)",
  "Program Head (Female)",
];

const COMMITTEE_MAP = {
  "Operation Committee": "Operation Committee",
  "National Program Head": "Program Head",
  "Other Member": "Other Members",
  "Other Members": "Other Members",
};

const normalizeValue = (value = "") => String(value).trim().toLowerCase();

const getValueName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value?.name || value?.title || value?.code || "").trim();
  }
  return String(value).trim();
};

const getCommitteeLabel = (member, isMarathwadaARegion = false) => {
  const committeeName = String(member?.committee || "").trim();
  const committeeType = String(member?.committeeType || "").trim();

  if (isMarathwadaARegion) {
    if (
      committeeName === "Operation Committee (Female)" ||
      committeeName === "Program Head (Female)"
    ) {
      return committeeName;
    }

    if (committeeName === "Operation Committee") {
      return "Operation Committee";
    }

    if (committeeName === "Program Head" || committeeType === "National Program Head") {
      return "Program Head";
    }
  }

  return COMMITTEE_MAP[committeeType] || committeeName || "";
};

const getFullName = (member) =>
  [member?.salutation || member?.Salutation, member?.userFirstName, member?.userMiddleName, member?.userLastName]
    .filter(Boolean)
    .join(" ");

const getMemberYear = (member) => {
  const rawDate = member?.joiningDate || member?.updatedAt;
  if (!rawDate) return "";

  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? "" : String(date.getFullYear());
};

const extractStringId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return extractStringId(value._id);
  }
  return "";
};

const getMemberProfileUserId = (member) =>
  extractStringId(member?.userId) ||
  extractStringId(member?.user?._id) ||
  extractStringId(member?.user) ||
  extractStringId(member?._id);

const getLatestUpdatedAt = (members = []) =>
  members.reduce((acc, member) => {
    const ts = member?.updatedAt || member?.joiningDate;
    if (!ts) return acc;

    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) {
      return acc;
    }

    return !acc || date > acc ? date : acc;
  }, null);

const formatUpdatedDate = (date) => {
  if (!date) return "";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const REC_FILTERS_STORAGE_KEY = "network_filters_rec";

const REC = ({ members = [], allowScopedRestore = false, canOpenMemberProfile = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey } = usePermission();
  const canDownloadExcel = canByKey(
    "COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DOWNLOAD_EXCEL",
  );
  const navigateFrom = location.pathname.includes("committeeMembersList")
    ? "committeeMembersList"
    : "network";

  const [selectedState, setSelectedState] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedCommittee, setSelectedCommittee] = useState("ALL");
  const [selectedYear, setSelectedYear] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (!allowScopedRestore) return;
    try {
      const saved = sessionStorage.getItem(REC_FILTERS_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      setSelectedState(parsed?.selectedState || "");
      setSelectedRegion(parsed?.selectedRegion || "");
      setSelectedCommittee(parsed?.selectedCommittee || "ALL");
      setSelectedYear(parsed?.selectedYear || "ALL");
      setSearchTerm(parsed?.searchTerm || "");
      setShowSearch(Boolean(parsed?.showSearch));
    } catch (error) {
      console.warn("Unable to restore REC filters", error);
    }
  }, [allowScopedRestore]);

  useEffect(() => {
    sessionStorage.setItem(
      REC_FILTERS_STORAGE_KEY,
      JSON.stringify({
        selectedState,
        selectedRegion,
        selectedCommittee,
        selectedYear,
        searchTerm,
        showSearch,
      }),
    );
  }, [
    selectedState,
    selectedRegion,
    selectedCommittee,
    selectedYear,
    searchTerm,
    showSearch,
  ]);

  const states = useMemo(
    () =>
      [...new Set(members.map((member) => getValueName(member?.state)).filter(Boolean))].sort(),
    [members]
  );

  const regions = useMemo(
    () =>
      [...new Set(
        members
          .filter((member) => getValueName(member?.state) === selectedState)
          .map((member) => getValueName(member?.region))
          .filter(Boolean)
      )].sort(),
    [members, selectedState]
  );

  const regionScopedMembers = useMemo(
    () =>
      members.filter(
        (member) =>
          getValueName(member?.state) === selectedState &&
          getValueName(member?.region) === selectedRegion
      ),
    [members, selectedRegion, selectedState]
  );

  const hasSelectedState = Boolean(selectedState);
  const hasSelectedRegion = Boolean(selectedRegion);
  const isMarathwadaARegion =
    normalizeValue(selectedRegion) === normalizeValue(MARATHWADA_A_REGION);

  const committeeOrder = isMarathwadaARegion
    ? MARATHWADA_A_COMMITTEE_ORDER
    : COMMITTEE_ORDER;

  const committees = useMemo(
    () =>
      [...new Set(
        regionScopedMembers
          .map((item) => getCommitteeLabel(item, isMarathwadaARegion))
          .filter(Boolean)
          .sort()
      )],
    [regionScopedMembers, isMarathwadaARegion]
  );

  const years = useMemo(
    () =>
      [...new Set(regionScopedMembers.map(getMemberYear).filter(Boolean))].sort(
        (a, b) => Number(b) - Number(a)
      ),
    [regionScopedMembers]
  );

  const canShowMembers = hasSelectedState && hasSelectedRegion;
  const hasRegionData = regionScopedMembers.length > 0;
  const disableAdvancedFilters = !hasRegionData;

  const filteredMembers = useMemo(() => {
    if (!canShowMembers) {
      return [];
    }

    const lowerSearch = searchTerm.toLowerCase();

    return regionScopedMembers.filter((member) => {
      const committeeMatch =
        selectedCommittee === "ALL" ||
        getCommitteeLabel(member, isMarathwadaARegion) === selectedCommittee;
      const memberYear = getMemberYear(member);
      const yearMatch = selectedYear === "ALL" || memberYear === selectedYear;
      const fullName = getFullName(member).toLowerCase();
      const searchMatch = fullName.includes(lowerSearch);

      return committeeMatch && yearMatch && searchMatch;
    });
  }, [
    canShowMembers,
    regionScopedMembers,
    searchTerm,
    selectedCommittee,
    selectedYear,
    isMarathwadaARegion,
  ]);

  const visibleUpdatedAt = useMemo(
    () => getLatestUpdatedAt(filteredMembers),
    [filteredMembers]
  );

  const groupedMembers = useMemo(() => {
    const grouped = committeeOrder.reduce((acc, committee) => {
      acc[committee] = [];
      return acc;
    }, {});

    filteredMembers.forEach((member) => {
      const committee = getCommitteeLabel(member, isMarathwadaARegion);
      if (!committee) return;
      if (!grouped[committee]) {
        grouped[committee] = [];
      }
      grouped[committee].push(member);
    });

    return grouped;
  }, [filteredMembers, committeeOrder, isMarathwadaARegion]);

  const getCommitteeStyle = (committee) =>
    theme.committeeStyles?.[committee] || theme.committeeStyles?.default;

  const handleExport = () => {
    exportNetworkMembersCsv(filteredMembers, "network_rec_members");
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, mt: 3 }}>
      <Box
      // sx={{
      //   borderRadius: 4,
      //   px: isMobile ? 1 : 2,
      // }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              lg: "row",
            },
            justifyContent: "space-between",
            alignItems: {
              xs: "stretch",
              lg: "center",
            },
            gap: 2,
          }}
        >
          {/* Filters */}
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 1.5,

              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, 200px)",
              },

              flexGrow: 1,
            }}
          >
            <TextField
              select
              size="small"
              label="State"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedRegion("");
                setSelectedCommittee("ALL");
                setSelectedYear("ALL");
                setSearchTerm("");
                setShowSearch(false);
              }}
              sx={{ minWidth: { xs: "100%", lg: 200 }, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
            >
              <MenuItem value="">Select State</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Region"
              value={selectedRegion}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setSelectedCommittee("ALL");
                setSelectedYear("ALL");
                setSearchTerm("");
                setShowSearch(false);
              }}
              disabled={!hasSelectedState}
              sx={{ minWidth: { xs: "100%", lg: 200 }, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
            >
              <MenuItem value="">Select Region</MenuItem>
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Committee"
              value={selectedCommittee}
              onChange={(e) => setSelectedCommittee(e.target.value)}
              disabled={disableAdvancedFilters}
              sx={{ minWidth: { xs: "100%", lg: 200 }, flex: { xs: "1 1 100%", sm: "1 1 220px" } }}
            >
              <MenuItem value="ALL">All Committees</MenuItem>
              {committees.map((committee) => (
                <MenuItem key={committee} value={committee}>
                  {committee}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={disableAdvancedFilters}
              sx={{ minWidth: { xs: "100%", lg: 200 }, flex: { xs: "1 1 100%", sm: "1 1 220px", } }}
            >
              <MenuItem value="ALL">All Years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,

              width: {
                xs: "100%",
                lg: "auto",
              },

              justifyContent: {
                xs: "flex-end",
                lg: "flex-end",
              },
            }}
          >


            {canDownloadExcel && (
              <Tooltip title="Download Excel" arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleExport}
                    disabled={!filteredMembers.length}
                    aria-label="Download Excel"
                    sx={{
                      textTransform: "none",
                      //borderRadius: 2,
                    }}
                  >
                    Download
                  </Button>
                </span>
              </Tooltip>
            )}
            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                disabled={disableAdvancedFilters}
                sx={{ width: { xs: "100%", md: 220 } }}
              />
            ) : (
              <IconButton
                onClick={() => setShowSearch(true)}
                disabled={disableAdvancedFilters}
              >
                <SearchIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>


      {!canShowMembers && (
        <Box
          sx={{
            px: 2,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Select a state and region to view REC members
          </Typography>
        </Box>
      )}

      {canShowMembers && !hasRegionData && (
        <Box
          sx={{
            px: 2,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            No REC members found for the selected state and region
          </Typography>
        </Box>
      )}

      {committeeOrder.map((committee) => {
        const membersList = groupedMembers[committee];
        if (!membersList || membersList.length === 0) return null;

        const style = getCommitteeStyle(committee);

        return (
          <Box
            key={committee}
            sx={{
              borderRadius: 4,
              mb: 4,
              px: 2,
              py: 2,
              background: style?.gradient,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                {committee}
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {membersList.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member._id || getFullName(member)}>
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: canOpenMemberProfile ? "pointer" : "default",
                      "&:hover": canOpenMemberProfile
                        ? {
                          boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                        }
                        : {},
                    }}
                    onClick={() => {
                      const profileUserId = getMemberProfileUserId(member);
                      if (canOpenMemberProfile && profileUserId) {
                        setNetworkRestoreOnce();
                        navigate(`/profile/${profileUserId}`, {
                          state: {
                            mode: "view",
                            navigateFrom,
                            canOpenMemberProfile,
                          },
                        });
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: "hidden",
                        background: style?.solid,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={getPublicS3Url(member?.userImageURL) || dummyImage}
                        alt={getFullName(member)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>{getFullName(member)}</Typography>

                      <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                        {member?.position || ""}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
      {visibleUpdatedAt && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatUpdatedDate(visibleUpdatedAt)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default REC;
